import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import DebouncedLoader from '../common/DebouncedLoader';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import {
    GET_RESOURCE_LIBRARY_SITE_PAGE_ITEM_QUERY,
    getResourceLibrarySitePageItemQueryVariables,
} from '../../graphql/pimcore/modules/resourceLibrary/queries/getResourceLibrarySitePageItemQuery';
import { findLocale, LocaleCode } from '../../utils/Locale';
import { transformResourceLibrarySitePageItemResponse } from '../../graphql/pimcore/modules/resourceLibrary/tools/transformResourceLibrarySitePageItemResponse';
import {
    ResourceLibrarySitePageItem,
    isSitePageApplicationArticleItem,
    isSitePageGenericItem,
} from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';
import SitePage from './SitePage';

interface SitePageResourcePageProps {
    id: string;
    locale: string;
}

const SitePageResourcePage: React.FC<SitePageResourcePageProps> = ({ id, locale }) => {
    const localeCode: LocaleCode = findLocale(locale).locale;
    const navigate = useNavigate();

    const { loading, error, data } = useQuery(GET_RESOURCE_LIBRARY_SITE_PAGE_ITEM_QUERY, {
        variables: getResourceLibrarySitePageItemQueryVariables(localeCode, Number(id)),
        client: pimcoreClient,
    });

    React.useEffect(() => {
        if (error) {
            console.error('GraphQL Site Page Query Error:', error);
        }
    }, [error]);

    const sitePageItem = useMemo<ResourceLibrarySitePageItem | null>(() => {
        if (!data) {
            return null;
        }
        return transformResourceLibrarySitePageItemResponse(data);
    }, [data]);

    // Redirect to 404 on error or when item could not be validated/loaded
    React.useEffect(() => {
        if (!loading && (error || !sitePageItem)) {
            navigate('/404_error', { replace: true });
        }
    }, [loading, error, sitePageItem, navigate]);

    const renderTemplate = (item: ResourceLibrarySitePageItem) => {
        if (isSitePageApplicationArticleItem(item) || isSitePageGenericItem(item)) {
            return <SitePage item={item} />;
        }

        const templateName = item.template;
        return <div className="mid-width">Unsupported site page template: {templateName}</div>;
    };

    return (
        <DebouncedLoader loading={loading} delay={800} size={40} centered={true}>
            {!error && sitePageItem && renderTemplate(sitePageItem)}
        </DebouncedLoader>
    );
};

export default SitePageResourcePage;
